import { formatCurrency } from "../../include/money";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { Close, ConfirmationNumber, Redeem, Payments, PeopleAlt, CreditCard, LocalOffer, RateReview, EmojiEvents, AccountBalanceWallet, PointOfSale, QrCode2, Groups } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { callPostApi } from "../../function/ApiCallFunction";
import GradientLoader from "../../components/CommanComponents/GradientLoader";
import { DataContext } from "../../context/DataContext";
import { consoleColors } from "../admin/consoleTheme";
import { CASHI_TAGLINE } from "../../include/cashiBrand";
import SaleLinkBox from "../report/SaleLinkBox";

const empty = {
  total_customer: 0,
  total_issued: 0,
  total_redeem: 0,
  total_physical_card: 0,
  total_offers: 0,
  total_reviews: 0,
  total_luckydraw: 0,
  total_sales: 0,
  total_sale_amount: 0,
  wallet_amount: 0,
  chart: [],
  club: null,
  club_members: [],
};

function MemberCard({ store }) {
  return (
    <Box
      sx={{
        border: `1px solid ${store.this_store ? consoleColors.accent : consoleColors.line}`,
        background: store.this_store ? "#f0f9ff" : "#f8fafc",
        borderRadius: 2,
        p: 1.3,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 750 }}>
        {store.store_name}{store.this_store ? " · This store" : ""}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: consoleColors.muted, mt: 0.3 }}>
        {[store.business_type_name, store.city_name, store.owner_name].filter(Boolean).join(" · ") || "Club store"}
      </Typography>
    </Box>
  );
}

export function ClubMembers({ club, members }) {
  const [open, setOpen] = useState(false);
  const list = members || [];
  if (!club || !list.length) return null;
  const preview = list.slice(0, 3);
  const extra = list.length - preview.length;
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 3, p: 2, mb: 1.6 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
        <Groups sx={{ fontSize: 20, color: consoleColors.accent }} />
        <Typography sx={{ fontSize: 15, fontWeight: 750, flex: 1 }}>Club members · {club.title}</Typography>
        {extra > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            sx={{
              border: 0,
              background: "transparent",
              color: consoleColors.accent,
              font: "inherit",
              fontSize: 14,
              fontWeight: 750,
              cursor: "pointer",
              p: 0,
              textDecoration: "underline",
              "&:hover": { color: consoleColors.accentHover },
            }}
          >
            More
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 12.5, color: consoleColors.muted, mb: 1.4, lineHeight: 1.45 }}>
        {list.length} {list.length === 1 ? "store" : "stores"} in this club. Loyalty can be used across different business types.
        {club.code ? ` Joining code ${club.code}.` : ""}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 1 }}>
        {preview.map((store) => (
          <MemberCard key={store.store_id} store={store} />
        ))}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1, fontWeight: 750 }}>
          Club members · {club.title}
          <IconButton onClick={() => setOpen(false)} aria-label="Close"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 1, pb: 2.2 }}>
          {list.map((store) => (
            <MemberCard key={store.store_id} store={store} />
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function money(value) {
  return formatCurrency(value);
}

function Bars({ rows, series }) {
  const max = Math.max(1, ...rows.flatMap((row) => series.map((item) => Number(row[item.key] || 0))));
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(rows.length, 1)}, 1fr)`, gap: 0.8, alignItems: "end", height: 168 }}>
      {rows.map((row) => (
        <Box key={row.date} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.7, height: "100%" }}>
          <Box sx={{ flex: 1, width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "3px" }}>
            {series.map((item) => (
              <Box key={item.key} title={`${item.label}: ${row[item.key]}`} sx={{ width: series.length > 1 ? 8 : 16, maxWidth: "40%", borderRadius: "6px 6px 2px 2px", height: `${Math.max(6, (Number(row[item.key] || 0) / max) * 100)}%`, background: item.color }} />
            ))}
          </Box>
          <Typography sx={{ fontSize: 11, color: consoleColors.muted, lineHeight: 1 }}>{row.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [datar, setDatar] = useState(empty);
  const { loginData } = useContext(DataContext);
  const storeid = loginData?.store_info?.store_id;
  const storeName = loginData?.store_info?.store_name || "Your store";
  const plan = String(loginData?.store_info?.cashi_plus_flag || "0");

  const dashboardApi = useCallback(async () => {
    if (!storeid) return;
    setLoading(true);
    try {
      const apiResult = await callPostApi("cashi-dashboard", { mod: "CASHI_DASHBOARD_INFO", data_arr: { store_id: storeid } });
      if (apiResult.status === "200" && apiResult.data) setDatar({ ...empty, ...apiResult.data, chart: apiResult.data.chart || [] });
    } catch (err) {
      console.error("Dashboard API failed", err);
    } finally {
      setLoading(false);
    }
  }, [storeid]);

  useEffect(() => { dashboardApi(); }, [dashboardApi]);

  const allCards = [
    { key: "customers", label: "Customers", value: datar.total_customer, to: "/customerList", icon: PeopleAlt, tint: "#e0f2fe", color: "#0369a1" },
    { key: "issued", label: "Issued coupons", value: datar.total_issued, to: "/issuedList", icon: ConfirmationNumber, tint: "#e4f4fd", color: "#21a7ec" },
    { key: "redeem", label: "Redeemed coupons", value: datar.total_redeem, to: "/redeemedList", icon: Redeem, tint: "#dcfce7", color: "#15803d" },
    { key: "sales", label: "Sales", value: datar.total_sales, to: "/saleReport", icon: PointOfSale, tint: "#ede9fe", color: "#6d28d9" },
    { key: "amount", label: "Sale amount, 7 days", value: money(datar.total_sale_amount), to: "/saleReport", icon: Payments, tint: "#fef3c7", color: "#b45309" },
    { key: "cards", label: "Physical cards", value: datar.total_physical_card, to: "/physicalCard", icon: CreditCard, tint: "#ffedd5", color: "#c2410c" },
    { key: "offers", label: "Offers", value: datar.total_offers, to: "/couponlist", icon: LocalOffer, tint: "#fce7f3", color: "#be185d" },
    { key: "reviews", label: "Reviews", value: datar.total_reviews, to: "/reviewList", icon: RateReview, tint: "#ecfccb", color: "#4d7c0f" },
    { key: "draws", label: "Lucky Draw Coupons", value: datar.total_luckydraw, to: "/luckydraws", icon: EmojiEvents, tint: "#fae8ff", color: "#a21caf" },
    { key: "plus", label: "Cashi Plus", value: "QR", to: "/dashboardPlus", icon: QrCode2, tint: "#e4f4fd", color: "#21a7ec" },
    { key: "wallet", label: "Wallet", value: money(datar.wallet_amount), to: "/wallet", icon: AccountBalanceWallet, tint: "#ccfbf1", color: "#0f766e" },
  ];
  const planKeys = plan === "3"
    ? ["customers", "draws", "sales", "amount", "wallet"]
    : plan === "1"
      ? ["customers", "issued", "redeem", "sales", "amount", "cards", "offers", "reviews", "draws", "plus", "wallet"]
      : ["customers", "issued", "redeem", "sales", "amount", "cards", "offers", "reviews", "draws", "wallet"];
  const cards = planKeys.map((key) => allCards.find((card) => card.key === key)).filter(Boolean);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2.2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, color: consoleColors.accent }}>{storeName}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: consoleColors.ink, lineHeight: 1.2 }}>Store Dashboard</Typography>
        <Typography sx={{ fontSize: 14, color: consoleColors.muted, mt: 0.6 }}>{CASHI_TAGLINE}</Typography>
      </Box>
      <SaleLinkBox storeId={storeid} />
      <ClubMembers club={datar.club} members={datar.club_members} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(5, 1fr)" }, gap: 1.4 }}>
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Box
              key={card.key}
              onClick={() => navigate(card.to)}
              sx={{
                background: "#fff",
                border: `1px solid ${consoleColors.line}`,
                borderRadius: 3,
                p: 1.7,
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 24px rgba(15,23,42,0.08)" },
                transition: "0.16s ease",
              }}
            >
              <Box sx={{ width: 36, height: 36, borderRadius: 2, background: card.tint, color: card.color, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.3 }}>
                <Icon sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: consoleColors.ink, lineHeight: 1.1 }}>{card.value}</Typography>
              <Typography sx={{ fontSize: 12.5, color: consoleColors.muted, mt: 0.5 }}>{card.label}</Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: plan === "3" ? "1fr" : "1.2fr 1fr" }, gap: 1.4, mt: 1.6 }}>
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 3, p: 2 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 750, mb: 0.3 }}>Sales this week</Typography>
          <Typography sx={{ fontSize: 12.5, color: consoleColors.muted, mb: 1.5 }}>Order amount for the last 7 days</Typography>
          <Bars rows={datar.chart} series={[{ key: "sales", label: "Sales", color: consoleColors.accent }]} />
        </Box>
        {plan !== "3" && (
          <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 3, p: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 750, mb: 0.3 }}>Coupons this week</Typography>
            <Box sx={{ display: "flex", gap: 1.4, mb: 1.5 }}>
              <Typography sx={{ fontSize: 12.5, color: consoleColors.accent }}>Issued</Typography>
              <Typography sx={{ fontSize: 12.5, color: "#15803d" }}>Redeemed</Typography>
            </Box>
            <Bars rows={datar.chart} series={[{ key: "issued", label: "Issued", color: consoleColors.accent }, { key: "redeemed", label: "Redeemed", color: "#22c55e" }]} />
          </Box>
        )}
      </Box>
      {loading && <GradientLoader text="Loading" />}
    </Box>
  );
}

export default Dashboard;
